/**
 * Fachliche Zeitberechnung der Wiedergabe.
 *
 * Rein funktional: keine Abhängigkeit von AudioContext, DOM oder
 * Browser-APIs. Alle Zeiten sind in Sekunden relativ zum Patternanfang.
 *
 * Das Raster ergibt sich aus den festen Meta-Eigenschaften des Modells
 * (ein Takt, 4/4, Sechzehntel): 4 Zählzeiten × 4 Sechzehntel = 16 Schritte.
 */

import { PATTERN_META } from '../model/serialization';
import { STEP_COUNT } from '../model/types';

/** Anzahl der Takte im Pattern. */
export const BARS = PATTERN_META.bars;

/** Zählzeiten (Viertel) pro Takt. */
export const BEATS_PER_BAR = PATTERN_META.timeSignature.numerator;

/** Gesamtzahl der Rasterschritte im Pattern (über alle Takte). */
export const TOTAL_STEPS = STEP_COUNT;

/** Rasterschritte je Zählzeit (Sechzehntel-Unterteilung). */
export const STEPS_PER_BEAT = TOTAL_STEPS / (BARS * BEATS_PER_BAR);

function assertBpm(bpm: number): void {
  if (!Number.isFinite(bpm) || bpm <= 0) {
    throw new RangeError(`Ungueltiges Tempo: ${String(bpm)}`);
  }
}

/** Dauer einer Zählzeit (Viertel) in Sekunden. */
export function beatDurationSeconds(bpm: number): number {
  assertBpm(bpm);
  return 60 / bpm;
}

/** Dauer eines Rasterschritts (Sechzehntel) in Sekunden. */
export function stepDurationSeconds(bpm: number): number {
  return beatDurationSeconds(bpm) / STEPS_PER_BEAT;
}

/** Startzeit eines Schrittes relativ zum Patternanfang, in Sekunden. */
export function stepStartSeconds(step: number, bpm: number): number {
  if (!Number.isInteger(step * 3) || step < 0) {
    throw new RangeError(`Ungueltiger Schritt: ${String(step)}`);
  }
  return step * stepDurationSeconds(bpm);
}

/** Gesamtdauer eines vollständigen Pattern-Durchlaufs in Sekunden. */
export function patternDurationSeconds(bpm: number): number {
  return TOTAL_STEPS * stepDurationSeconds(bpm);
}
